/**
 * AnalyticsStatusToolbarFilter — CERTIFICATE status, promoted from drawer-only to a
 * compact toolbar control (VERA-260).
 *
 * ⚠️ THE SHAPE NOW LIVES IN `AnalyticsChipToolbarFilter`. This file is one of TWO thin
 * callers of it; the other is `AnalyticsScanStatusToolbarFilter` (the scan-outcome axis).
 * Everything that was once documented here about the control's anatomy — why the popover
 * body is the real `StatusChipFilter` rather than a lookalike, why the trigger's inline
 * chips are `StatusChip` and not `StatusPill`, why the trigger is content-sized rather
 * than truncating, why the label is rendered at rest — is on that component, because it
 * is true of both facets and a second copy of it would drift. Read it there.
 *
 * What is left here is what is specific to THIS facet: which redux key it writes and what
 * its label is.
 *
 * ## One state, two access paths
 *
 * This does NOT own a second copy of the status selection. It reads/writes the exact same
 * `state.analytics.filters.certificateStatuses` redux key the drawer's `StatusChipFilter`
 * field already does — `AnalyticsFiltersHeader` collapses/expands both through the SAME
 * `collapseStatuses` / `expandStatuses` pair (see that module), so a change made here is
 * instantly reflected in the drawer chip row and vice versa, and the applied-filter chip
 * clears identically from either surface.
 *
 * ## "Certificate status" — the label AGREES with the drawer and the chip
 *
 * ⚠️ This shipped briefly as a bare **"Status"**, described here as a deliberate three-way
 * split justified on space. That justification did not survive measurement and the split is
 * gone; the note is kept only so the reversal is not re-litigated.
 *
 * The argument for the longer form was always the strong one, and it got stronger: this
 * page counts TWO things (scans AND certificates), a scan carries its OWN status
 * vocabulary (Verified / Invalid / Opened), and as of VERA-260 that vocabulary is a real
 * filter sitting IMMEDIATELY BESIDE this one. A bare "Status" named neither dimension when
 * it was alone in the row; with a sibling control it would be actively ambiguous. The
 * drawer field and the applied-filter chip both say "Certificate Status", and §44 (one
 * label per field, never two) says they must not diverge.
 *
 * What was claimed against it was width — an ESTIMATE of ~110px, made without a browser.
 * The band was then measured, and the answer changed: the toolbar's drop thresholds are
 * derived from the band's real content width (`AnalyticsFiltersHeader`), so a longer label
 * is paid for by those constants rather than by compressing the control or splitting the
 * vocabulary. Sentence case is the only difference from the drawer, which is weight, not
 * naming — the noun is what §44 governs.
 */
import React from 'react'

import { CircleDotIcon } from 'lucide-react'

import { CERTIFICATE_STATUSES } from 'constants/statuses'

import { AnalyticsChipToolbarFilter } from './AnalyticsChipToolbarFilter'
import { CERT_STATUS_OPTIONS, FILTER_FIELDS } from './analyticsFilterFields'

interface AnalyticsStatusToolbarFilterProps {
  /** The COLLAPSED selection — `collapseStatuses(filters.certificateStatuses)`, the same
   *  shape the drawer's `StatusChipFilter` reads. */
  value: string[]
  /** Fires with the next COLLAPSED selection; caller expands it back before dispatching. */
  onChange: (next: string[]) => void
}

export const AnalyticsStatusToolbarFilter = ({ value, onChange }: AnalyticsStatusToolbarFilterProps) => (
  <AnalyticsChipToolbarFilter
    label="Certificate status"
    icon={CircleDotIcon}
    options={CERT_STATUS_OPTIONS}
    statuses={CERTIFICATE_STATUSES}
    popoverTitle="Certificate Status"
    field={FILTER_FIELDS.status}
    value={value}
    onChange={onChange}
  />
)

export default AnalyticsStatusToolbarFilter
